import { NotificationCenter, NotificationHandler, NotificationType } from '@/assets/libraries/NotificationHandler';
import { useModalStore } from '@/stores/modal';
import { _getAllRows, _deleteRow, OfflineModeEditsPendingSyncManager } from './OfflineMode';

// Storage thresholds
const STORAGE_THRESHOLDS = {
	WARNING: 90, // Start cleanup
	CRITICAL: 95, // Show notification
	EMERGENCY: 98, // Show uncloseable modal
};

// Revision retention rules
const REVISION_RULES = {
	MIN_AGE_DAYS: 14, // Never delete revisions < 14 days
	RETENTION_DAYS: 30, // Keep every 10th revision < 30 days
	KEEP_EVERY_NTH: 10, // Keep every 10th revision
};

// Throttling for notifications
let last_storage_depletion_notification_time = 0;
const STORAGE_DEPLETION_NOTIFICATION_THROTTLE = 10000; // 10 seconds

class StorageManagementService {
	constructor() {
		this.is_cleanup_in_progress = false;
		this.is_emergency_modal_shown = false;
		this.emergency_modal_store = null;
	}

	/**
	 * Main storage management function - called from handleStorageQuotaExceeded
	 */
	async manageStorage() {
		try {
			const { quota, usage } = await navigator.storage.estimate();
			const storage_percentage = (usage / quota) * 100;

			console.log(`[[STORAGE MANAGEMENT]] Using ${(usage / 1048576).toFixed(2)} out of ${(quota / 1048576).toFixed(2)} MB (${storage_percentage.toFixed(2)}% of quota)`);

			// Handle different storage levels
			if (storage_percentage >= STORAGE_THRESHOLDS.EMERGENCY) {
				await this.handleEmergencyStorage(storage_percentage);
			} else if (storage_percentage >= STORAGE_THRESHOLDS.CRITICAL) {
				this.handleCriticalStorage(storage_percentage);
			} else if (storage_percentage >= STORAGE_THRESHOLDS.WARNING) {
				await this.handleWarningStorage(storage_percentage);
			}
		} catch (error) {
			console.error('Error in storage management:', error);
		}
	}

	/**
	 * Handle emergency storage (≥98%) - Show uncloseable modal
	 */
	async handleEmergencyStorage(storage_percentage) {
		if (this.is_emergency_modal_shown) return;

		this.is_emergency_modal_shown = true;

		const modal_store = useModalStore();
		const modal_object = {
			title: {
				label: 'Storage Emergency - Action Required',
			},
			paragraphs: [
				{
					label: `Your local storage COBRA Show Creator space is ${storage_percentage.toFixed(1)}% full. You cannot continue scripting until storage space is freed up.`,
				},
				{
					label: 'If offline, please connect to the internet to sync your changes and free up storage space, or manually clear some data. If this issue persists, please contact support.',
				},
			],
			// buttons: [
			// 	{
			// 		label: 'Go Online to Sync',
			// 		action: () => {
			// 			// Trigger offline sync
			// 			OfflineModeEditsPendingSyncManager.syncOfflineEdits().then(() => {
			// 				// Recheck storage after sync
			// 				this.manageStorage({ from: 'emergency_sync' });
			// 			});
			// 		},
			// 		style: 'bg-blue-600 hover:bg-blue-700',
			// 	},
			// ],
			options: {
				disable_dismissal: true, // Make modal uncloseable
				destroy_on_dismissal: false,
			},
		};

		modal_store.createInteractiveModal(modal_object);

		// Store reference to modal store for later closing
		this.emergency_modal_store = modal_store;

		// Following was added to test the modal closing functionality, but it's not needed for now.
		// setTimeout(() => {
		// 	this.checkAndResetEmergencyModal();
		// }, 10000);
	}

	/**
	 * Handle critical storage (≥95%) - Show throttled notification + cleanup
	 */
	handleCriticalStorage(storage_percentage) {
		const current_time = Date.now();
		const time_since_last_notification = current_time - last_storage_depletion_notification_time;

		if (time_since_last_notification >= STORAGE_DEPLETION_NOTIFICATION_THROTTLE) {
			NotificationCenter.add(new NotificationHandler.Notification('Offline Storage Depletion', 'You are over 95% full on local storage for COBRA Show Creator and will not be able to continue scripting if it fills up anymore, if offline,please go online to sync changes. If this issue persists, please contact support.', null, NotificationType.ERROR));
			last_storage_depletion_notification_time = current_time;
		}

		// Also trigger cleanup at critical level for more aggressive cleaning
		this.handleWarningStorage(storage_percentage);
	}

	/**
	 * Handle warning storage (≥90%) - Start cleanup process
	 */
	async handleWarningStorage(storage_percentage) {
		if (this.is_cleanup_in_progress) return;

		this.is_cleanup_in_progress = true;

		try {
			// First, run offline sync if there are pending changes
			await this.runOfflineSyncIfNeeded();

			// Then perform cleanup
			await this.performRevisionCleanup(storage_percentage);
		} catch (error) {
			console.error('Error during storage cleanup:', error);
		} finally {
			this.is_cleanup_in_progress = false;
		}
	}

	/**
	 * Run offline sync if there are pending changes
	 */
	async runOfflineSyncIfNeeded() {
		try {
			const pending_edits = await OfflineModeEditsPendingSyncManager.getAllEditsPendingSync();
			if (pending_edits.length > 0) {
				console.log(`[[STORAGE CLEANUP]] Running offline sync before cleanup (${pending_edits.length} pending edits)`);
				await OfflineModeEditsPendingSyncManager.syncOfflineEdits();
			}
		} catch (error) {
			console.error('Error running offline sync before cleanup:', error);
		}
	}

	/**
	 * Perform revision cleanup based on retention rules
	 */
	async performRevisionCleanup(current_storage_percentage) {
		console.log('[[STORAGE CLEANUP]] Performing revision cleanup');
		console.log('[[STORAGE CLEANUP]] Current storage percentage:', current_storage_percentage);

		try {
			const all_revisions = await _getAllRows('show_revisions');
			if (!all_revisions || all_revisions.length === 0) {
				console.log('[[STORAGE CLEANUP]] No revisions to clean up');
				return;
			}

			// Get last successful sync timestamp
			const last_successful_sync = this.getLastSuccessfulSyncTimestamp();

			// Sort revisions by age (oldest first)
			const sorted_revisions = all_revisions.sort((a, b) => a.revision_saved_at - b.revision_saved_at);

			// Filter revisions that can be deleted
			const candidates_for_deletion = this.filterRevisionsForDeletion(sorted_revisions, last_successful_sync);
			// let check = candidates_for_deletion.map((revision) => {
			// 	return {
			// 		...revision,
			// 		time_value: new Date(revision.revision_saved_at),
			// 	};
			// });
			console.log('debug filter revision 2', candidates_for_deletion);

			if (candidates_for_deletion.length === 0) {
				console.log('[[STORAGE CLEANUP]] No revisions eligible for deletion');
				return;
			}

			console.log(`[[STORAGE CLEANUP]] Found ${candidates_for_deletion.length} revisions eligible for deletion`);

			// Delete revisions until we're below 90%
			let deleted_count = 0;
			for (const revision of candidates_for_deletion) {
				try {
					await _deleteRow('show_revisions', revision.revision_id);
					deleted_count++;

					// Check if we've freed enough space
					const { quota, usage } = await navigator.storage.estimate();
					const new_storage_percentage = (usage / quota) * 100;

					if (new_storage_percentage < STORAGE_THRESHOLDS.WARNING) {
						break;
					}
				} catch (error) {
					console.error(`Error deleting revision ${revision.revision_id}:`, error);
				}
			}

			console.log(`[[STORAGE CLEANUP]] Deleted ${deleted_count} revisions`);

			// Notify user about cleanup
			if (deleted_count > 0) {
				NotificationCenter.add(new NotificationHandler.Notification('Storage Cleanup Complete', `Freed up local storage space by removing ${deleted_count} old local revisions.`, null, NotificationType.SUCCESS, { expire_time: 5000 }));
			}
		} catch (error) {
			console.error('Error during revision cleanup:', error);
		}
	}

	/**
	 * Filter revisions based on retention rules
	 */
	filterRevisionsForDeletion(revisions, last_successful_sync) {
		// Initialization: Calculate age thresholds and initialize counter
		const now = new Date().getTime(); // Current timestamp in milliseconds
		const min_age_ms = REVISION_RULES.MIN_AGE_DAYS * 24 * 60 * 60 * 1000; // 14 days = 1,209,600,000 ms
		const retention_age_ms = REVISION_RULES.RETENTION_DAYS * 24 * 60 * 60 * 1000; // 30 days = 2,592,000,000 ms

		console.log('[[USING REVISION THAT MAY/MAYNOT BE DELETED]]', revisions);
		let index_of_revisions_14_to_30_days = 0; // Counter for "every 10th" logic in 14-30 day range

		return revisions.filter((revision) => {
			const revision_age = now - revision.revision_saved_at;

			// Never delete revisions < 14 days old
			console.log('debug filter revision', revision_age < min_age_ms, last_successful_sync && revision.revision_saved_at > last_successful_sync, revision_age > retention_age_ms);
			// console.log('debug filter revision 1', revision_age, last_successful_sync, revision.revision_saved_at, min_age_ms, retention_age_ms);

			if (revision_age < min_age_ms) {
				return false;
			}

			// TODO : what if last_successful_sync is undefined for some reason, not sure if that's possible. Need to discuss this with the team.

			// Don't delete revisions newer than last successful sync
			if (last_successful_sync && revision.revision_saved_at > last_successful_sync) {
				return false;
			}
			console.log('debug filter revision 0', revision_age, retention_age_ms);

			// For revisions >= 14 days but < 30 days old, keep every 10th revision
			if (revision_age >= min_age_ms && revision_age <= retention_age_ms) {
				// Keep every 10th revision in this range
				// If revision_id is 1749734277449 → 1749734277449 % 10 = 9 → 9 !== 0 → DELETE
				// If revision_id is 1749734288100 → 1749734288100 % 10 = 0 → 0 !== 0 → KEEP
				if (index_of_revisions_14_to_30_days % REVISION_RULES.KEEP_EVERY_NTH !== 0) {
					index_of_revisions_14_to_30_days++;
					return true; // DELETE
				}
				index_of_revisions_14_to_30_days++;
				return false; // KEEP
			}

			// For revisions > 30 days old, delete all (no retention required)
			if (revision_age > retention_age_ms) {
				return true; // DELETE
			}

			// Should not reach here, but return false as safety
			return false;
		});
	}

	/**
	 * Get last successful sync timestamp
	 */
	getLastSuccessfulSyncTimestamp() {
		try {
			return parseInt(localStorage.getItem('last_successful_offline_sync') || '0');
		} catch (error) {
			console.error('Error getting last successful sync timestamp:', error);
			return 0;
		}
	}

	/**
	 * Set last successful sync timestamp
	 */
	setLastSuccessfulSyncTimestamp() {
		try {
			localStorage.setItem('last_successful_offline_sync', Date.now().toString());
		} catch (error) {
			console.error('Error setting last successful sync timestamp:', error);
		}
	}

	/**
	 * Reset emergency modal state and close modal
	 */
	resetEmergencyModalState() {
		this.is_emergency_modal_shown = false;

		// Actually close the modal on screen
		if (this.emergency_modal_store != undefined && this.emergency_modal_store != null) {
			this.emergency_modal_store.closeModal();
			this.emergency_modal_store = null;
		}
	}

	/**
	 * Check if emergency modal should be dismissed after sync
	 */
	async checkAndResetEmergencyModal() {
		if (!this.is_emergency_modal_shown) return;

		try {
			const { quota, usage } = await navigator.storage.estimate();
			const storage_percentage = (usage / quota) * 100;
			console.log('[[STORAGE MANAGEMENT]] Checking emergency modal state - storage now at', storage_percentage.toFixed(2) + '%');
			// If storage dropped below emergency threshold, reset modal state
			if (storage_percentage < STORAGE_THRESHOLDS.EMERGENCY) {
				this.resetEmergencyModalState();

				console.log('[[STORAGE MANAGEMENT]] Emergency modal state reset - storage now at', storage_percentage.toFixed(2) + '%');

				// Show success notification
				NotificationCenter.add(new NotificationHandler.Notification('Storage Emergency Resolved', `Storage has been reduced to ${storage_percentage.toFixed(1)}%. You can now continue scripting.`, null, NotificationType.SUCCESS, { expire_time: 5000 }));
			}
		} catch (error) {
			console.error('Error checking emergency modal state:', error);
		}
	}
}

// Export singleton instance
export const storage_management_service = new StorageManagementService();
